import { ChangeDetectionStrategy, Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { Subscription, of } from 'rxjs';
import { catchError, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';

import { SettingsStore } from '@core/settings';
import { AnalyticEvent, AnalyticsService } from '@core/analytics';
import { LocalizationStore } from '@core/localization';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

import { CataloguesStore } from '@features/assets/data-access/catalogues.store';
import { SearchApiService } from '@features/assets/data-access/search-api.service';
import {
    ISearchHitDto,
    ISearchLayoutDto,
    ISearchResultDto,
    SearchAssetType,
} from '@features/assets/data-access/search-api.dto';
import { DetailsService } from '@features/details/data-access/details.service';
import { AssetSearchService } from '@features/assets/data-access/asset-search.service';

import { AssetGrid } from '@features/assets/components/asset-grid/asset-grid';
import { AssetList } from '@features/assets/components/asset-list/asset-list';
import { CatalogueRowSkeleton } from '@features/assets/components/catalogue-row-skeleton/catalogue-row-skeleton';
import { IAssetListItem } from '@features/assets/components/asset-card/asset-list-item.types';

export enum SearchTypes {
    Grid = 0,
    Categorized = 1,
}

/** Minimum query length before the API is hit. */
const MIN_QUERY_LENGTH = 2;

/** Copy shown while the query is too short. Supplied by the backend dictionary. */
const SHORT_HINT_KEY = 'search.minlength.message';

interface ICategorySection {
    readonly type: number;
    readonly title: string;
    readonly items: readonly ISearchHitDto[];
}

@Component({
    selector: 'asset-search',
    templateUrl: './asset-search.component.html',
    styleUrls: ['./asset-search.component.scss'],
    standalone: true,
    imports: [AssetGrid, AssetList, CatalogueRowSkeleton, TranslocoPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetSearchComponent implements OnDestroy {
    private readonly api = inject(SearchApiService);
    private readonly searchBus = inject(AssetSearchService);
    private readonly detailsService = inject(DetailsService);
    private readonly settingsStore = inject(SettingsStore);
    private readonly analytics = inject(AnalyticsService);
    private readonly transloco = inject(TranslocoService);
    private readonly localization = inject(LocalizationStore);
    protected readonly store = inject(CataloguesStore);

    protected readonly SearchTypes = SearchTypes;
    protected readonly MIN_QUERY_LENGTH = MIN_QUERY_LENGTH;
    // Reactive, not a one-shot read: this component lives in the app SHELL, so it is
    // constructed before the boot settings fetch lands. A constructor-time read of
    // storage.settings saw null on a cold start and locked the whole session into the
    // Grid fallback — categorized search then only worked after a reload, once
    // sessionStorage already held the previous boot's settings (AVIION-36647).
    protected readonly searchType = computed<SearchTypes>(
        () => this.settingsStore.elasticSearchResultType() === 1 ? SearchTypes.Categorized : SearchTypes.Grid,
    );

    protected readonly loading = signal(false);
    protected readonly query = signal('');
    protected readonly layout = signal<ISearchLayoutDto>([]);
    protected readonly results = signal<readonly ISearchHitDto[]>([]);
    // Held on the shared service, not here: the mini-details overlay reports clicks too.
    private readonly searchId = this.searchBus.searchId;

    /** Recommendations are surfaced when there's no active query (default landing view). */
    protected readonly recommendationsItems = computed<readonly IAssetListItem[]>(
        () => this.store.recommendations().vod?.items ?? [],
    );

    /** Group flat results by SearchAssetType using the layout's order + display name. */
    protected readonly categorized = computed<readonly ICategorySection[]>(() => {
        const items = this.results();
        if (!items.length) return [];

        const buckets = new Map<number, ISearchHitDto[]>();
        for (const item of items) {
            const bucket = bucketTypeOf(item.type);
            if (!buckets.has(bucket)) buckets.set(bucket, []);
            buckets.get(bucket)!.push(item);
        }

        const layout = this.layout();
        if (layout.length) {
            return [...layout]
                .sort((a, b) => a.order - b.order)
                .map(entry => ({
                    type: entry.type,
                    title: entry.name,
                    items: buckets.get(entry.type) ?? [],
                }))
                .filter(s => s.items.length > 0);
        }

        // Layout not loaded yet (or backend returned empty) — still render items grouped by
        // their raw type with no titles. Keeps results visible during the layout race.
        return [...buckets.entries()]
            .sort(([a], [b]) => a - b)
            .map(([type, sectionItems]) => ({ type, title: '', items: sectionItems }));
    });

    protected readonly trimmedLength = computed(() => this.query().trim().length);
    protected readonly hasMinQuery = computed(() => this.trimmedLength() >= MIN_QUERY_LENGTH);
    protected readonly hasResults = computed(() => this.results().length > 0);
    protected readonly showRecommendations = computed(() => this.trimmedLength() === 0 && !this.loading());
    protected readonly showShortHint = computed(
        () => this.trimmedLength() > 0 && this.trimmedLength() < MIN_QUERY_LENGTH,
    );
    /**
     * Empty while the backend dictionary carries no entry for the key. Transloco echoes an unknown
     * key back, so the hint used to read "search.minlength.message" on screen (AVIION-36333); showing
     * nothing is better, and the copy appears by itself once the translation is added.
     */
    protected readonly shortHintText = computed(() => {
        this.localization.activeLanguageId(); // re-resolve after a language switch
        const text = this.transloco.translate(SHORT_HINT_KEY);
        return text === SHORT_HINT_KEY ? '' : text;
    });
    protected readonly showEmpty = computed(
        () => this.hasMinQuery() && !this.loading() && !this.hasResults(),
    );

    private subs = new Subscription();

    constructor() {
        if (document.body.classList.contains('programme-no-scroll')) {
            document.body.classList.remove('programme-no-scroll');
        }

        this.store.loadRecommendations('vod');

        // Load layout unconditionally so categorized rendering is ready when results arrive,
        // regardless of `searchType` (which can flip via storage settings at runtime).
        this.subs.add(
            this.api.getLayout()
                .pipe(catchError(() => of([] as ISearchLayoutDto)))
                .subscribe(layout => this.layout.set(layout ?? [])),
        );

        // Query bus. Min 2 chars before the API is hit; shorter values just clear results.
        // A repeat of the previous value (Enter after the debounce already fired) is dropped
        // without a second API call. The memory is per instance: this view is destroyed when
        // search closes, so reopening it and typing the last term searches again.
        const empty: ISearchResultDto = { items: [], searchId: null, hideBadge: false };
        this.subs.add(this.searchBus.searchChange.pipe(
            distinctUntilChanged(),
            tap((q: string) => this.query.set(q ?? '')),
            switchMap((q: string) => {
                const trimmed = (q ?? '').trim();
                if (trimmed.length < MIN_QUERY_LENGTH) {
                    this.loading.set(false);
                    this.results.set([]);
                    this.searchId.set(null);
                    return of<ISearchResultDto>(empty);
                }
                this.loading.set(true);
                this.analytics.logEvent(AnalyticEvent.SearchQueryEntered, { search_term: trimmed });
                return this.api.search(trimmed).pipe(catchError(() => of<ISearchResultDto>(empty)));
            }),
            tap(() => this.loading.set(false)),
        ).subscribe(res => {
            const items = res?.items ?? [];
            this.results.set(items);
            this.searchId.set(res?.searchId ?? null);

            if (!this.hasMinQuery()) return;
            if (items.length) {
                this.analytics.logEvent(AnalyticEvent.SearchResultImpression);
            } else {
                this.analytics.logEvent(AnalyticEvent.NoSearchResults);
                window.scrollTo(0, 0);
            }
        }));
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
        // Nothing on screen belongs to that result set any more.
        this.searchId.set(null);
    }

    protected onItemClick(item: IAssetListItem): void {
        this.searchBus.trackClick(item as ISearchHitDto);
        this.detailsService.open({ item });
    }
}

/** Collapses the granular SearchAssetType codes (Series=3, Season=5, Episode=6) into a single bucket. */
function bucketTypeOf(type: number | null | undefined): number {
    switch (type) {
        case SearchAssetType.Season:
        case SearchAssetType.Episode:
            return SearchAssetType.Series;
        default:
            return type ?? SearchAssetType.Vod;
    }
}
